import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  HORMONE_TRENDS_SERIES,
  HORMONE_TRENDS_SERIES_ORDER,
  HORMONE_TRENDS_SERIES_STYLES,
  getVisibleHormoneSeries,
} from '../../../common/utils/services/hormone-trends-utils';
import { HormoneTrendsSeriesId } from '../../../common/model/type/hormone-trends.type';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

export interface OvHormoneSeriesChipsProps {
  /** `null` means no hormone is isolated — the chart shows them all. */
  focusedSeriesId: HormoneTrendsSeriesId | null;
  onFocusChange: (seriesId: HormoneTrendsSeriesId | null) => void;
  bbtEnabled: boolean;
  onBbtToggle: () => void;
}

const OvHormoneSeriesChips: FC<OvHormoneSeriesChipsProps> = ({
  focusedSeriesId,
  onFocusChange,
  bbtEnabled,
  onBbtToggle,
}) => {
  const { t } = useTranslation();
  const visibleSeries = getVisibleHormoneSeries(focusedSeriesId, bbtEnabled);

  return (
    <Container role="group" aria-label={t('hormoneTrends.seriesChips.label')}>
      {HORMONE_TRENDS_SERIES_ORDER.map((seriesId) => {
        const series = HORMONE_TRENDS_SERIES[seriesId];
        const style = HORMONE_TRENDS_SERIES_STYLES[seriesId];
        const isFocused = seriesId === focusedSeriesId;
        // Pressed tracks what the chart draws, not what is focused, so a lit
        // chip always means "this line is on the chart".
        const isVisible = visibleSeries.includes(seriesId);

        return (
          <Chip
            key={seriesId}
            type="button"
            aria-pressed={isVisible}
            $colour={style.colour}
            $isSelected={isVisible}
            onClick={() =>
              series.isOverlay
                ? onBbtToggle()
                : onFocusChange(isFocused ? null : seriesId)
            }
          >
            <Swatch
              $colour={style.colour}
              $isDashed={!!style.strokeDasharray}
            />
            <ChipName>{series.displayName}</ChipName>
            <ChipUnit>{series.unit}</ChipUnit>
          </Chip>
        );
      })}
    </Container>
  );
};

export default OvHormoneSeriesChips;

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`;

const Chip = styled.button<{ $colour: string; $isSelected: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.75rem;
  border-radius: ${Variables.borderRadius.XLARGE};
  border: 1px solid
    ${({ $colour, $isSelected }) =>
      $isSelected ? $colour : Colours.OV_BORDER_COLOR_V3};
  background-color: ${({ $isSelected }) =>
    $isSelected ? Colours.OV_WHITE : 'transparent'};
  color: ${Colours.OV_BASE};
  font-size: ${Variables.fontSizes.MEDIUM};
  line-height: ${Variables.lineHeights.LARGE};
  cursor: pointer;
  opacity: ${({ $isSelected }) => ($isSelected ? 1 : 0.55)};

  &:hover {
    opacity: 1;
  }
`;

const Swatch = styled.span<{ $colour: string; $isDashed: boolean }>`
  width: 1rem;
  border-top: 0.1875rem ${({ $isDashed }) => ($isDashed ? 'dashed' : 'solid')}
    ${({ $colour }) => $colour};
`;

const ChipName = styled.span`
  font-weight: bold;
`;

const ChipUnit = styled.span`
  font-size: ${Variables.fontSizes.SMALL};
  color: ${Colours.OV_CHARCOAL};
`;
